import { toast } from '@postscript/components';
import { merchantShareUrl, withMerchantLinks } from 'utils/adminHost';

const TABLE_PARTS = ['TR', 'TBODY', 'THEAD', 'TFOOT', 'TABLE'];

// A selection inside one link's text clones without the link, so the link is
// kept. Selected table rows clone without their <table>, and Docs and Sheets
// paste bare rows as plain text, so the table ancestors are kept too.
export const selectionHtml = (selection: Selection) => {
  const template = document.createElement('template');
  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    let fragment: Node = range.cloneContents();
    let ancestor: Node | null = range.commonAncestorContainer;
    const link = (
      ancestor instanceof Element ? ancestor : ancestor.parentElement
    )?.closest('a[href]');
    if (link) {
      const wrapper = link.cloneNode(false);
      wrapper.appendChild(fragment);
      fragment = wrapper;
      ancestor = link.parentNode;
    }
    while (
      ancestor instanceof Element &&
      TABLE_PARTS.includes(ancestor.tagName)
    ) {
      const wrapper = ancestor.cloneNode(false);
      wrapper.appendChild(fragment);
      fragment = wrapper;
      ancestor = ancestor.tagName === 'TABLE' ? null : ancestor.parentNode;
    }
    template.content.appendChild(fragment);
  }
  return template.innerHTML;
};

const writeHtml = async (html: string, text: string) => {
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    return navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
  }
  // No async clipboard API (Firefox before 127): fill both types from a
  // one-shot copy event.
  const onCopy = (event: ClipboardEvent) => {
    event.preventDefault();
    event.clipboardData?.setData('text/html', html);
    event.clipboardData?.setData('text/plain', text);
  };
  document.addEventListener('copy', onCopy);
  try {
    if (!document.execCommand('copy')) throw new Error('Copy failed');
  } finally {
    document.removeEventListener('copy', onCopy);
  }
  return undefined;
};

// Copies merchant links for sending outside the company: the selection with its
// links rewritten, or the page link when nothing with app links is selected.
export const copyMerchantLink = () => {
  const selection = window.getSelection();
  const text = selection?.toString() ?? '';
  // react-hotkeys ignores key presses in inputs, textareas and contenteditable,
  // so the selection here is page content.
  if (selection && text) {
    const { html, count, rewritten } = withMerchantLinks(
      selectionHtml(selection),
      window.location.href,
    );
    if (count > 0) {
      writeHtml(html, text)
        .then(() =>
          toast.success(
            rewritten
              ? `Copied ${rewritten} merchant link${
                  rewritten === 1 ? '' : 's'
                }.`
              : "Copied the selection. Its links can't be shared.",
          ),
        )
        .catch(() => toast.error("Couldn't copy the merchant links."));
      return;
    }
  }

  const shareUrl = merchantShareUrl(window.location.href);
  if (!shareUrl) {
    toast.error("This page can't be shared as a link.");
    return;
  }
  if (!navigator.clipboard) {
    toast.error("Couldn't copy the merchant link.");
    return;
  }
  navigator.clipboard
    .writeText(shareUrl)
    .then(() => toast.success('Merchant link copied.'))
    .catch(() => toast.error("Couldn't copy the merchant link."));
};
